import { api, setAuthTokens } from './client';
import type {
  ApiContestState,
  ApiDifficulty,
  ApiSqlDialect,
  AuthResponse,
  CareerDetailResponse,
  CareerSummaryResponse,
  ContestDetailResponse,
  ContestMeResponse,
  ContestProblemResponse,
  ContestRegistrationResponse,
  ContestStandingResponse,
  ContestSummaryResponse,
  CourseDetailResponse,
  CourseAccessResponse,
  CourseProgressResponse,
  CourseSummaryResponse,
  CustomTablePayload,
  DiscussionResponse,
  EditorialResponse,
  LeaderboardEntryResponse,
  ArticleRunResponse,
  LearnPostResponse,
  LearnPostSummary,
  LessonResponse,
  LikeResponse,
  PlaybackResponse,
  MeProgressResponse,
  MeStatsResponse,
  MeSubmissionResponse,
  OfficialSolutionResponse,
  PageResponse,
  ProblemDetailResponse,
  ProblemSummaryResponse,
  PublicStatsResponse,
  RunResponse,
  SiteNavItemResponse,
  SubmissionAcceptedResponse,
  SubmissionResponse,
  TopicCountResponse,
  UserResponse,
} from './types';

/* =========================================================================
   Tipizatsiyalangan API endpointlari — sqluz-api bilan mos.
   Mavjud: auth, problems, submissions, progress/stats/heatmap, contests,
   leaderboard. Hali yo'q: editorial/hints/solutions.
   ========================================================================= */

export interface ProblemQuery {
  page?: number;
  size?: number;
  difficulty?: ApiDifficulty;
  topic?: string;
  isPremium?: boolean;
  search?: string;
  lang?: string;
}

export const authApi = {
  // Ro'yxatdan o'tish 1-bosqich: user yaratilmaydi, emailga kod yuboriladi
  register: (payload: { email: string; username: string; password: string; displayName?: string }) =>
    api.post<void>('/auth/register', payload),
  // 2-bosqich: kod to'g'ri bo'lsa — user yaratiladi va tokenlar qaytadi
  verifyEmail: (email: string, code: string) =>
    api.post<AuthResponse>('/auth/verify-email', { email, code }),
  resendVerification: (email: string) => api.post<void>('/auth/verify-email/resend', { email }),
  forgotPassword: (email: string) => api.post<void>('/auth/forgot-password', { email }),
  resetPassword: (email: string, code: string, newPassword: string) =>
    api.post<void>('/auth/reset-password', { email, code, newPassword }),
  login: (email: string, password: string) => api.post<AuthResponse>('/auth/login', { email, password }),
  refresh: (refreshToken: string) => api.post<AuthResponse>('/auth/refresh', { refreshToken }),
  logout: (refreshToken: string) => api.post<void>('/auth/logout', { refreshToken }),
  me: () => api.get<UserResponse>('/auth/me'),
};

export const problemsApi = {
  list: (query: ProblemQuery) => api.get<PageResponse<ProblemSummaryResponse>>('/problems', { params: query }),
  detail: (slug: string, lang: string) =>
    api.get<ProblemDetailResponse>(`/problems/${slug}`, { params: { lang } }),
  topics: () => api.get<TopicCountResponse[]>('/problems/topics'),
  editorial: (slug: string) => api.get<EditorialResponse>(`/problems/${slug}/editorial`),
  solution: (slug: string) => api.get<OfficialSolutionResponse>(`/problems/${slug}/solution`),
  run: (slug: string, sourceCode: string, dialect: ApiSqlDialect) =>
    api.post<RunResponse>(`/problems/${slug}/run`, { sourceCode, dialect }),
  /** Foydalanuvchi tahrirlagan ma'lumot (INSERT lar) ustida ishga tushirish — solishtiruvsiz. */
  runCustom: (
    slug: string,
    sourceCode: string,
    tables: CustomTablePayload[],
    dialect: ApiSqlDialect,
  ) => api.post<RunResponse>(`/problems/${slug}/run/custom`, { sourceCode, tables, dialect }),
  submit: (slug: string, sourceCode: string, dialect: ApiSqlDialect) =>
    api.post<SubmissionAcceptedResponse>(`/problems/${slug}/submissions`, { sourceCode, dialect }),
  /** Joriy foydalanuvchining shu masaladagi jo'natmalari (JWT talab qilinadi). */
  mySubmissions: (slug: string, page = 0, size = 20) =>
    api.get<PageResponse<SubmissionResponse>>(`/problems/${slug}/submissions`, {
      params: { page, size },
    }),
};

export const submissionsApi = {
  getById: (id: number) => api.get<SubmissionResponse>(`/submissions/${id}`),
};

export const meApi = {
  /** Joriy foydalanuvchining masalalar bo'yicha progressi (JWT talab qilinadi). */
  progress: () => api.get<MeProgressResponse>('/me/progress'),
  /** Sotib olingan kurslar (kasb orqali ham) va kasblar. */
  courseAccess: () => api.get<CourseAccessResponse>('/me/course-access'),
  /** Profil statistikasi: solved, streak, heatmap, mavzular. */
  stats: () => api.get<MeStatsResponse>('/me/stats'),
  /** Barcha masalalar bo'yicha so'nggi jo'natmalar. */
  submissions: (page = 0, size = 10) =>
    api.get<PageResponse<MeSubmissionResponse>>('/me/submissions', { params: { page, size } }),
};

export interface ContestQuery {
  state?: ApiContestState;
  page?: number;
  size?: number;
  lang?: string;
}

export const contestsApi = {
  list: (query: ContestQuery = {}) =>
    api.get<PageResponse<ContestSummaryResponse>>('/contests', { params: query }),
  detail: (slug: string, lang: string) =>
    api.get<ContestDetailResponse>(`/contests/${slug}`, { params: { lang } }),
  problems: (slug: string, lang: string) =>
    api.get<ContestProblemResponse[]>(`/contests/${slug}/problems`, { params: { lang } }),
  problemDetail: (contestSlug: string, problemSlug: string, lang: string) =>
    api.get<ProblemDetailResponse>(`/contests/${contestSlug}/problems/${problemSlug}`, {
      params: { lang },
    }),
  register: (slug: string) => api.post<ContestRegistrationResponse>(`/contests/${slug}/register`),
  unregister: (slug: string) => api.delete<void>(`/contests/${slug}/register`),
  standings: (slug: string, page = 0, size = 50) =>
    api.get<PageResponse<ContestStandingResponse>>(`/contests/${slug}/standings`, {
      params: { page, size },
    }),
  me: (slug: string) => api.get<ContestMeResponse>(`/contests/${slug}/me`),
  submit: (contestSlug: string, problemSlug: string, sourceCode: string, dialect: ApiSqlDialect) =>
    api.post<SubmissionAcceptedResponse>(
      `/contests/${contestSlug}/problems/${problemSlug}/submissions`,
      { sourceCode, dialect },
    ),
};

export const discussionsApi = {
  list: (slug: string) => api.get<DiscussionResponse[]>(`/problems/${slug}/discussions`),
  create: (slug: string, body: string, parentId?: number | null) =>
    api.post<DiscussionResponse>(`/problems/${slug}/discussions`, { body, parentId: parentId ?? null }),
  delete: (id: number) => api.delete<void>(`/discussions/${id}`),
  like: (id: number) => api.post<LikeResponse>(`/discussions/${id}/like`),
};

export const coursesApi = {
  list: () => api.get<CourseSummaryResponse[]>('/courses'),
  detail: (slug: string) => api.get<CourseDetailResponse>(`/courses/${slug}`),
  /** Qulflangan darsda videoRef/contentMd bo'lmaydi (server tekshiradi). */
  lesson: (courseSlug: string, lessonSlug: string) =>
    api.get<LessonResponse>(`/courses/${courseSlug}/lessons/${lessonSlug}`),
  /** Shifrlangan HLS dars uchun imzolangan playlist URL (qulflangan darsda 403). */
  playback: (courseSlug: string, lessonSlug: string) =>
    api.get<PlaybackResponse>(`/courses/${courseSlug}/lessons/${lessonSlug}/playback`),
  /** Tugatilgan darslar (JWT talab qilinadi). */
  progress: (courseSlug: string) => api.get<CourseProgressResponse>(`/courses/${courseSlug}/progress`),
  setCompleted: (courseSlug: string, lessonSlug: string, completed: boolean) =>
    completed
      ? api.put<CourseProgressResponse>(`/courses/${courseSlug}/lessons/${lessonSlug}/complete`)
      : api.delete<CourseProgressResponse>(`/courses/${courseSlug}/lessons/${lessonSlug}/complete`),
};

export const careersApi = {
  list: () => api.get<CareerSummaryResponse[]>('/careers'),
  detail: (slug: string) => api.get<CareerDetailResponse>(`/careers/${slug}`),
};

export const learnApi = {
  /** Nashr qilingan postlar (yangisi birinchi); topic — faqat shu mavzu */
  list: (topic?: string) => api.get<LearnPostSummary[]>('/learn', { params: topic ? { topic } : undefined }),
  /** Pullik postda (Premium'siz) contentMd bo'lmaydi (server tekshiradi). */
  detail: (slug: string) => api.get<LearnPostResponse>(`/learn/${slug}`),
  /** Tahrirlangan misol; SQL xatosi javobda `error`, limit — 429 */
  run: (slug: string, blockId: string, query: string) =>
    api.post<ArticleRunResponse>(`/learn/${slug}/run`, { blockId, query }),
};

export const leaderboardApi = {
  list: (page = 0, size = 50) =>
    api.get<PageResponse<LeaderboardEntryResponse>>('/leaderboard', { params: { page, size } }),
};

export const statsApi = {
  /** Home "Raqamlarda" — jonli public sanoqlar (auth talab qilinmaydi). */
  overview: () => api.get<PublicStatsResponse>('/stats'),
};

export const siteApi = {
  /** Menyu bandlari ko'rinishi (auth talab qilinmaydi). */
  nav: () => api.get<SiteNavItemResponse[]>('/site/nav'),
};

/** Logout uchun yordamchi: refresh token bo'lsa serverga xabar beradi, keyin lokal tokenlarni tozalaydi. */
export async function logoutAndClearTokens(refreshToken: string | null) {
  if (refreshToken) {
    try {
      await authApi.logout(refreshToken);
    } catch {
      // Server tomonda xato bo'lsa ham lokal sessiyani tozalashda davom etamiz
    }
  }
  setAuthTokens(null, null);
}
